import { getMarket } from './utils-markets.js';
import { getViewedProducts, addToViewedProducts } from './utils-recently-viewed-products.js';

class RecentlyViewedProducts extends HTMLElement {
  /**
   * @type {Array} handles - An array of recently viewed product handles.
   */
  handles = getViewedProducts();
  currentProductHandle = this.getAttribute('data-current-handle') || '';

  /**
   * @method connectedCallback
   * @description Called when the element is connected to the DOM.
   */
  connectedCallback() {
    // Save the current product to recently viewed if we have a handle
    if (this.currentProductHandle) {
      this.handles = addToViewedProducts(this.currentProductHandle);
    }
    this.renderBasedOnHandles();
  }

  /**
   * @method removeLoaders
   * @description Removes the loaders from the recently viewed products if the products are loaded. If not the fallback products will be shown.
   */
  removeLoaders() {
    // A small timeout is used to ensure the innerHTML operation has completed to ensure the animation works.
    setTimeout(() => {
      this.querySelectorAll('[data-loader]').forEach((loader) => {
        loader.setAttribute('aria-hidden', true);
      });
    }, 50);
  }

  /**
   * @method renderBasedOnHandles
   * @description Render based on the localStorage handles.
   */
  async renderBasedOnHandles() {
    // Filter the current product handle from the handles array.
    const handlesToRender = this.handles.filter((handle) => handle !== this.currentProductHandle);

    // If there are no handles, remove the loaders and return.
    if (!handlesToRender.length) {
      this.removeLoaders();
      return;
    }

    // Fetch the product card for each handle.
    const htmlCardArray = await Promise.all(
      handlesToRender.map(async (handle) => {
        const card = await this.fetchProductCard(handle);
        return card;
      })
    ).catch((error) => {
      console.log(error);
      console.warn('Error fetching product cards');
      this.removeLoaders();
    });

    // Add the cards to the swiper-wrapper.
    this.querySelector('.swiper-wrapper').innerHTML = htmlCardArray.join('');
    this.removeLoaders();
  }

  /**
   * @method fetchProductCard
   * @description Fetches the HTML content of a product card for a given product handle.
   * @param {string} handle - The handle of the product to fetch.
   * @returns {Promise<string>} The HTML content of the product card.
   */
  async fetchProductCard(handle) {
    try {
      const response = await fetch(
        `${location.origin}${getMarket()}products/${handle}?section_id=fetch-recently-viewed-product-card`
      );
      const html = await response.text();
      const parsedElement = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('.shopify-section')?.innerHTML;
      return parsedElement;
    } catch (error) {
      console.log(error);
      console.warn(`Error fetching product card ${handle}`);
    }
  }
}

customElements.define('recently-viewed-products', RecentlyViewedProducts);
